import type { ApolloCache, ApolloClient, Reference } from '@apollo/client';
import { InboxEntry } from '@this+that/models';

const TREE_QUERY_NAME = 'getInboxEntryTree';
const INBOX_ENTRY_TYPENAME = 'InboxEntry';
export const TREE_REFETCH_DEBOUNCE_MS = 400;

const TREE_SYNC_FIELDS = [
  'subject',
  'description',
  'resolvedAt',
  'entryKind',
  'dueAt',
  'priority',
  'parentInboxEntryId',
  'childOrderKey',
  'childProgress',
  'specKey',
  'specStatus',
  'specReviewers',
  'requirementRef',
  'participantIds',
  'taskLabelIds',
  'agentDelegation',
  'updatedAt',
  'version',
] as const satisfies ReadonlyArray<keyof InboxEntry>;

const TYPENAME_FIELD = '__typename';
const AGENT_DELEGATION_TYPENAME = 'AgentDelegation';
const SPEC_REVIEWER_TYPENAME = 'SpecReviewer';

// A raw object written into the cache has no __typename, so a fragment on its type never matches it.
const TYPENAME_BY_FIELD: Partial<
  Record<(typeof TREE_SYNC_FIELDS)[number], string>
> = {
  agentDelegation: AGENT_DELEGATION_TYPENAME,
  specReviewers: SPEC_REVIEWER_TYPENAME,
};

function withTypename(value: unknown, typename: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => withTypename(item, typename));
  }
  return value && typeof value === 'object'
    ? { ...value, [TYPENAME_FIELD]: typename }
    : value;
}

function toCacheValue(
  key: (typeof TREE_SYNC_FIELDS)[number],
  value: unknown,
): unknown {
  const typename = TYPENAME_BY_FIELD[key];
  return typename ? withTypename(value, typename) : value;
}

export interface TreeCacheMembership {
  isKnown: boolean;
  isParentKnown: boolean;
}

export function appendChildrenToTreeCache(
  cache: ApolloCache,
  parentInboxEntryId: string,
  children: InboxEntry[],
): void {
  cache.modify({
    fields: {
      [TREE_QUERY_NAME]: (
        existing: readonly Reference[] = [],
        { readField, toReference },
      ) => {
        const knownIds = new Set(existing.map((ref) => readField('id', ref)));
        if (!knownIds.has(parentInboxEntryId)) {
          return existing;
        }
        const added = children
          .filter((child) => !knownIds.has(child.id))
          .map((child) =>
            toReference({ __typename: INBOX_ENTRY_TYPENAME, id: child.id }),
          )
          .filter((ref): ref is Reference => ref !== undefined);
        return added.length > 0 ? [...existing, ...added] : existing;
      },
    },
  });
}

export function readTreeCacheMembership(
  cache: ApolloCache,
  item: InboxEntry,
): TreeCacheMembership {
  const membership: TreeCacheMembership = {
    isKnown: false,
    isParentKnown: false,
  };
  cache.modify({
    fields: {
      [TREE_QUERY_NAME]: (
        existing: readonly Reference[] = [],
        { readField },
      ) => {
        for (const ref of existing) {
          const id = readField('id', ref);
          membership.isKnown ||= id === item.id;
          membership.isParentKnown ||= id === item.parentInboxEntryId;
        }
        return existing;
      },
    },
  });
  return membership;
}

// Entities in an open tree are normalized, so patching the entity re-renders the tree.
export function patchTreeEntryInCache(
  cache: ApolloCache,
  item: InboxEntry,
): void {
  const id = cache.identify({ __typename: INBOX_ENTRY_TYPENAME, id: item.id });
  if (!id) {
    return;
  }
  if (isOlderThanCached(cache, id, item)) {
    return;
  }
  const fields: Record<string, () => unknown> = {};
  for (const key of TREE_SYNC_FIELDS) {
    if (key in item) {
      fields[key] = () => toCacheValue(key, item[key] ?? null);
    }
  }
  cache.modify({ id, fields });
}

function isOlderThanCached(
  cache: ApolloCache,
  id: string,
  item: InboxEntry,
): boolean {
  let heldVersion: unknown;
  cache.modify({
    id,
    fields: {
      version: (value: unknown) => {
        heldVersion = value;
        return value;
      },
    },
  });
  return (
    typeof heldVersion === 'number' &&
    item.version != null &&
    item.version < heldVersion
  );
}

let refetchTimer: ReturnType<typeof setTimeout> | undefined;

// A burst (a bulk paste fans out one echo per child) collapses into one refetch.
export function scheduleTreeRefetch(client: ApolloClient): void {
  clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => {
    client.refetchQueries({ include: [TREE_QUERY_NAME] });
  }, TREE_REFETCH_DEBOUNCE_MS);
}

export function syncEntryIntoTreeCache(
  client: ApolloClient,
  item: InboxEntry,
): TreeCacheMembership {
  const membership = readTreeCacheMembership(client.cache, item);
  if (membership.isKnown && item.deletedAt == null) {
    patchTreeEntryInCache(client.cache, item);
  } else if (membership.isKnown || membership.isParentKnown) {
    scheduleTreeRefetch(client);
  }
  return membership;
}
